import {
  DEMO_CAMPAIGNS,
  DEMO_ISSUES,
  DEMO_MODE,
  DEMO_OVERVIEW,
  DEMO_PIPELINE,
  DEMO_SEGMENTS,
  DEMO_SYNC,
  demoFetch,
  filterDemoSubscribers,
} from "./demo-data";
import type { PreviewSection } from "./newsletter-html";
import type { TitleKind } from "./title-match";

export interface Subscriber {
  id: string;
  email: string | null;
  clevertap_identity: string | null;
  newsletter_opt_in: boolean | null;
  country: string | null;
  locale: string | null;
  synced_at: string;
  name?: string | null;
  member_type?: string | null;
  last_active?: string | null;
  interests?: string[];
  last_received?: string | null;
  last_opened?: string | null;
  last_clicked?: string | null;
  unsubscribed_from?: string | null;
}

export interface SyncStatus {
  source: string;
  last_run_at: string | null;
  last_success: string | null;
  rows_loaded: number | null;
  error: string | null;
}

export interface OverviewStats {
  total_subscribers: number;
  opted_in: number;
  opted_in_pct: number;
  subscriber_delta_pct?: number;
  opted_in_delta_pct?: number;
  open_rate_delta_pct?: number;
  countries_top: { country: string; count: number; share: number }[];
  last_issue: string;
  last_issue_open_rate: number;
  last_send_recipients?: number;
  source?: string;
}

export interface Segment {
  id: string;
  name: string;
  description: string;
  member_count: number;
  last_pushed_at: string | null;
  status: "synced" | "draft";
}

export interface NewsletterIssue {
  id: string;
  title: string;
  subject: string;
  status: "draft" | "in_review" | "approved" | "sent" | "scheduled";
  updated_at: string;
  author: string;
  articles: number;
  open_rate: number | null;
}

export interface Campaign {
  id: string;
  name: string;
  issue_title: string;
  segment_name: string;
  status: "draft" | "scheduled" | "queued" | "sending" | "sent" | "cancelled" | "failed" | "paused";
  scheduled_at: string | null;
  sent_at: string | null;
  recipients: number;
  open_rate: number | null;
}

export interface PipelineHealth {
  last_sync_at: string | null;
  last_sync_success: string | null;
  last_sync_error: string | null;
  profiles: number;
  consent_missing: number;
  consent_granted: number;
  explicit_interest: number;
  queued: number;
  sent: number;
  failed: number;
  blocked: number;
  suppressed: number;
  profile_events?: number;
  cio_waiting?: number;
  cio_active?: number;
  cio_removed?: number;
  monthly_sent: number;
  monthly_cap: number;
  send_controls_enabled: boolean;
}

export interface ApiMeta {
  demo?: boolean;
  source?: string;
}

export class UnauthorizedError extends Error {
  constructor() {
    super("Your session has expired.");
    this.name = "UnauthorizedError";
  }
}

let unauthorizedHandler: (() => void) | null = null;

/** AuthProvider registers here so a 401 re-checks the session. */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

/**
 * fetch for authenticated API routes. A 401 must never be mistaken for an
 * outage — falling back to demo fixtures there would show invented figures as
 * if they were live data.
 */
export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const r = await fetch(path, init);
  if (r.status === 401) {
    unauthorizedHandler?.();
    throw new UnauthorizedError();
  }
  return r;
}

export async function isDemoMode(): Promise<boolean> {
  if (!DEMO_MODE) return false;
  try {
    const r = await fetch("/api/v1/health", { signal: AbortSignal.timeout(1500) });
    if (r.ok) {
      const h = await r.json();
      return !!h.demo;
    }
  } catch {
    /* API offline — use client fixtures */
  }
  return true;
}

async function apiGet<T>(path: string, demoFn: () => Promise<T>): Promise<{ data: T; meta: ApiMeta }> {
  if (await isDemoMode()) {
    return { data: await demoFn(), meta: { demo: true, source: "demo/fixtures" } };
  }
  try {
    const r = await apiFetch(path);
    if (!r.ok) throw new Error(`${path} ${r.status}`);
    const body = await r.json();
    return { data: body.data ?? body, meta: body.meta ?? {} };
  } catch (err) {
    if (err instanceof UnauthorizedError) throw err;
    // Only a demo build may stand sample data in for a failed request. On the
    // live site that turned an outage into plausible numbers — and fake
    // newsletters the editor then saved into the real database.
    if (!DEMO_MODE) throw err;
    return { data: await demoFn(), meta: { demo: true, source: "demo/fallback" } };
  }
}

export async function fetchHealth(): Promise<{
  ok: boolean;
  demo: boolean;
  supabase: boolean;
  clevertap: boolean;
  email?: boolean;
  email_from?: string;
  environment?: string;
  /** Whether newsletters can reach subscribers, and what is missing if not. */
  delivery?: { ready: boolean; missing: string[]; from: string };
}> {
  if (DEMO_MODE) {
    try {
      const r = await fetch("/api/v1/health");
      if (r.ok) return r.json();
    } catch {
      /* no API: the demo answers for it */
    }
    return demoFetch({ ok: true, demo: true, supabase: false, clevertap: true, email: false });
  }
  const r = await fetch("/api/v1/health");
  if (!r.ok) throw new Error(`health ${r.status}`);
  return r.json();
}

export async function fetchOverview(): Promise<OverviewStats> {
  try {
    const r = await apiFetch("/api/v1/overview");
    if (r.ok) {
      const body = await r.json() as { data?: OverviewStats; meta?: { source?: string } };
      return { ...(body.data ?? (body as OverviewStats)), source: body.meta?.source };
    }
  } catch (err) {
    if (err instanceof UnauthorizedError) throw err;
    if (!DEMO_MODE) throw err;
  }
  if (!DEMO_MODE) throw new Error("The overview could not be loaded.");
  const data = await demoFetch(DEMO_OVERVIEW);
  return { ...data, source: "demo/fallback" };
}

export async function fetchSubscribers(q = "", limit = 50): Promise<{ rows: Subscriber[]; total: number; source?: string }> {
  const r = await apiFetch(`/api/v1/subscribers?limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ""}`);
  if (!r.ok) {
    if (!DEMO_MODE) throw new Error(`Subscribers could not be loaded (${r.status}).`);
    const data = await demoFetch(filterDemoSubscribers(q, limit));
    return { rows: data, total: data.length, source: "demo/fallback" };
  }
  const body = await r.json() as { data?: Subscriber[]; meta?: { total?: number; source?: string; demo?: boolean } };
  const rows = body.data ?? [];
  return {
    rows,
    total: body.meta?.total ?? rows.length,
    source: body.meta?.source,
  };
}

export async function fetchSyncStatus(): Promise<SyncStatus | null> {
  try {
    const r = await apiFetch("/api/v1/sync/status");
    if (r.ok) {
      const body = await r.json() as { data?: SyncStatus };
      return body.data ?? null;
    }
  } catch (err) {
    if (err instanceof UnauthorizedError) throw err;
  }
  // Unknown is not the same as a sample: on the live site say nothing.
  return DEMO_MODE ? demoFetch(DEMO_SYNC) : null;
}

export async function fetchSegments(): Promise<Segment[]> {
  const { data } = await apiGet("/api/v1/segments", () => demoFetch(DEMO_SEGMENTS));
  return data;
}

export async function fetchIssues(): Promise<NewsletterIssue[]> {
  const { data } = await apiGet("/api/v1/issues", () => demoFetch(DEMO_ISSUES));
  return data;
}

/**
 * A newsletter as `newsletter.newsletters` holds it. The editor works in
 * `IssueDetail`, which carries a few things the table does not store — how
 * many articles are pinned, the open rate of a send — so the two are mapped
 * rather than shared.
 */
export interface IssueRow {
  id: string;
  title: string;
  subject: string;
  preheader: string | null;
  status: NewsletterIssue["status"];
  blocks: unknown[] | null;
  sections: unknown[] | null;
  categories: string[] | null;
  notes: string | null;
  comments: unknown[] | null;
  versions: unknown[] | null;
  created_at: string;
  updated_at: string;
}

/**
 * Save a newsletter. The editor mints the id, so this is an upsert: saving the
 * same draft twice updates it rather than making a second one, and a save that
 * failed can simply be sent again.
 */
export async function saveIssue(row: {
  id: string;
  title: string;
  subject: string;
  preheader?: string | null;
  status: string;
  notes?: string | null;
  sections?: unknown[];
  blocks?: unknown[];
  categories?: string[];
  comments?: unknown[];
  versions?: unknown[];
}): Promise<IssueRow | null> {
  const r = await apiFetch("/api/v1/issues", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(row),
  });
  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    throw new Error(`Could not save the newsletter (${r.status})${detail ? `: ${detail.slice(0, 160)}` : ""}`);
  }
  const body = (await r.json()) as { data?: IssueRow };
  return body.data ?? null;
}

export async function deleteIssue(id: string): Promise<void> {
  const r = await apiFetch(`/api/v1/issues/${id}`, { method: "DELETE" });
  if (!r.ok) throw new Error(`Could not delete the newsletter (${r.status})`);
}

export async function fetchCampaigns(): Promise<Campaign[]> {
  const { data } = await apiGet("/api/v1/campaigns", () => demoFetch(DEMO_CAMPAIGNS));
  return data;
}

export async function fetchPipelineHealth(): Promise<PipelineHealth> {
  const { data } = await apiGet("/api/v1/pipeline/health", () => demoFetch(DEMO_PIPELINE));
  return data;
}

export async function sendIssuePreview(payload: {
  to: string;
  title: string;
  subject: string;
  notes?: string;
  preheader?: string;
  sender_name?: string;
  utm?: { source: string; medium: string; campaign: string; content?: string };
  articles: { title: string; url: string; category?: string }[];
  blocks?: { type: string; text?: string; url?: string; alt?: string }[];
  /** The newsletter's structure. Without it the email arrives empty. */
  sections?: PreviewSection[];
  category_heading?: string;
}): Promise<{ messageId: string; to: string; from: string }> {
  const r = await apiFetch("/api/v1/issues/preview-send", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = (await r.json().catch(() => ({}))) as {
    data?: { messageId: string; to: string; from: string };
    error?: string;
    code?: string;
  };
  if (!r.ok) {
    const extra = body.code ? ` (${body.code})` : "";
    throw new Error(`${body.error || `preview-send ${r.status}`}${extra}`);
  }
  if (!body.data) throw new Error("preview-send returned no data");
  return body.data;
}

export function isAbortError(err: unknown): boolean {
  return (err instanceof DOMException && err.name === "AbortError")
    || (err instanceof Error && err.name === "AbortError");
}

export interface ContentRecommendHit {
  kind: TitleKind;
  title: string;
  url?: string;
  category?: string;
  page_type?: string;
  score: number;
  source?: string;
  /** What the page says about itself — the editor's starting point. */
  excerpt?: string;
  /**
   * The catalogue has no picture column, so this is always absent from a
   * search. A picture comes from the article's own page, read when one is
   * chosen, or is pasted by the editor.
   */
  image?: string;
  read_minutes?: number;
}

export async function fetchContentRecommend(opts: {
  q?: string;
  /** Scopes results to a health category — the newsletter's audience. */
  category?: string;
  kinds?: string[];
  context?: string;
  exclude?: string[];
  signal?: AbortSignal;
}): Promise<ContentRecommendHit[]> {
  const params = new URLSearchParams();
  if (opts.category?.trim()) params.set("category", opts.category.trim().slice(0, 80));
  if (opts.q?.trim()) params.set("q", opts.q.trim().slice(0, 200));
  if (opts.kinds?.length) params.set("kinds", opts.kinds.join(","));
  if (opts.context?.trim()) params.set("context", opts.context.trim().slice(0, 500));
  if (opts.exclude?.length) params.set("exclude", opts.exclude.slice(0, 20).join(","));
  // Articles are never invented. Standing in fabricated titles and
  // altibbi.com URLs for a catalogue that returned nothing put made-up health
  // content in front of an editor as though it were real, ready to be pinned
  // into a newsletter and sent. An empty catalogue reads as empty.
  const r = await apiFetch(`/api/v1/content/recommend?${params}`, { signal: opts.signal });
  if (!r.ok) {
    throw new Error(`The article catalogue is not reachable (${r.status})`);
  }
  const body = (await r.json()) as { data?: ContentRecommendHit[] };
  return Array.isArray(body.data) ? body.data : [];
}

export interface CatalogueCategory {
  value: string;
  count: number;
}

/**
 * The categories that exist in the catalogue, from the same table the SEO
 * content pipeline fills. A hard-coded list here drifts from the taxonomy and
 * silently matches nothing.
 */
export async function fetchCatalogueCategories(
  signal?: AbortSignal,
): Promise<{ categories: CatalogueCategory[]; approximate: boolean }> {
  const r = await apiFetch("/api/v1/content/categories", { signal });
  if (!r.ok) throw new Error(`The content catalogue is not reachable (${r.status})`);
  const body = (await r.json()) as {
    data?: CatalogueCategory[];
    meta?: { approximate?: boolean };
  };
  return {
    categories: Array.isArray(body.data) ? body.data : [],
    approximate: Boolean(body.meta?.approximate),
  };
}

export interface LinkPreview {
  url: string;
  title?: string;
  image?: string;
  excerpt?: string;
  site?: string;
}

/**
 * Read an Altibbi article's own title, image and description so an editor
 * pasting a link does not retype what the page already says.
 *
 * Deliberately not routed through apiGet: there is no demo fallback worth
 * inventing here — a made-up title would be indistinguishable from a real one
 * and could go out in an email.
 */
export async function fetchLinkPreview(url: string): Promise<LinkPreview> {
  const res = await fetch(`/api/v1/content/link-preview?url=${encodeURIComponent(url)}`, {
    headers: { accept: "application/json" },
  });
  const body = await res.json().catch(() => null) as { data?: LinkPreview; error?: string } | null;
  if (!res.ok || !body?.data) {
    throw new Error(body?.error || `Could not read that page (${res.status})`);
  }
  return body.data;
}

export interface AudienceInterest {
  id: number;
  slug: string | null;
  name_en: string | null;
  name_ar: string | null;
  subscribers: number;
}

/**
 * The topics subscribers actually chose, from `newsletter.interests`.
 *
 * This is what a newsletter targets. The CMS files articles under its own
 * category names, which are not necessarily topics anyone can subscribe to,
 * so the audience list has to come from the subscriber side.
 */
export async function fetchAudienceInterests(
  signal?: AbortSignal,
): Promise<{ interests: AudienceInterest[]; approximate: boolean }> {
  const r = await apiFetch("/api/v1/audience/interests", { signal });
  if (!r.ok) throw new Error(`Subscriber interests are not reachable (${r.status})`);
  const body = (await r.json()) as {
    data?: AudienceInterest[];
    meta?: { approximate?: boolean };
  };
  return {
    interests: Array.isArray(body.data) ? body.data : [],
    approximate: Boolean(body.meta?.approximate),
  };
}
